import { isNavigationFailure, NavigationFailureType } from 'vue-router'
import type { AiNavigation } from '@/lib/ai'
import type { QueryCardTarget } from '@onxmaps/ai-toolkit/toolbox/definitions/mapQueryCard'
import type { WaypointDraft } from '@onxmaps/ai-toolkit/toolbox/definitions/mapWaypointCreate'
import router from '@/router'
import Geographic from '@/lib/geographic'
import { CARD_ORIGINS, MARKUP_CREATE_ORIGINS } from '@/lib/enums-marketing'
import icons from '@/lib/icons'
import { useCardsStore, CARD_LAYERS_DETAIL } from '@/stores/cards-store'
import routeNames from '@/fish/router/constants'

const CREATE_ROUTE_NAME = 'fish-waypoint-create'
const EDIT_ROUTE_NAME = 'fish-waypoint-edit'
const QUERY_OVERVIEW_ROUTE = 'query-fish-overview'
const PLACE_ZOOM = 14

const isAbortedOrCancelled = (failure: unknown): boolean =>
  isNavigationFailure(failure, NavigationFailureType.aborted) ||
  isNavigationFailure(failure, NavigationFailureType.cancelled)

/** Fish vertical's navigation actions for the onX Guide tools and result UI. */
export const navigation: AiNavigation = {
  openQueryCard: async (target: QueryCardTarget) => {
    const coord = new Geographic(
      target.lon,
      target.lat,
      target.zoom
    ).toURLCoordParamWithHeight()

    const failure = await router.push({
      name: QUERY_OVERVIEW_ROUTE,
      params: { coord },
      state: { animateMapTo: true, origin: CARD_ORIGINS.AI_GUIDE_QUERY_CARD },
    })
    return isAbortedOrCancelled(failure) ? 'aborted' : 'opened'
  },

  stageWaypoint: async (draft: WaypointDraft) => {
    const location = new Geographic(draft.lon, draft.lat).toURLCoordParam()

    const failure = await router.push({
      name: CREATE_ROUTE_NAME,
      query: { location },
      state: {
        origin: MARKUP_CREATE_ORIGINS.ONX_GUIDE,
        ...(draft.name ? { name: draft.name } : {}),
        ...(draft.icon ? { icon: draft.icon } : {}),
      },
    })
    return isAbortedOrCancelled(failure) ? 'aborted' : 'staged'
  },

  // Both create and edit routes render the same waypoint-edit-card, which reads
  // `query.location` only on mount. Re-pushing while open would reuse the live
  // instance without re-mounting — leaving the pin at the old location while
  // falsely reporting success — so callers must check this first.
  isWaypointCardOpen: () => {
    const currentRouteName = router.currentRoute?.value?.name
    return (
      typeof currentRouteName === 'string' &&
      [CREATE_ROUTE_NAME, EDIT_ROUTE_NAME].includes(currentRouteName)
    )
  },

  // fish-web's `getWaypointSelectorIcons(toSelect)` requires the argument (no
  // default); pass `''` so no icon is marked selected.
  getWaypointIconLabels: () =>
    icons
      .getWaypointSelectorIcons('')
      .map(({ label }: { label: string }) => label),

  openPlaceDetails: (coordinate) => {
    const [lon, lat] = coordinate
    const coord = new Geographic(
      lon,
      lat,
      PLACE_ZOOM
    ).toURLCoordParamWithHeight()

    router
      .push({
        name: QUERY_OVERVIEW_ROUTE,
        params: { coord },
        state: {
          animateMapTo: true,
          origin: CARD_ORIGINS.AI_GUIDE_FIND_PLACES,
        },
      })
      .catch(() => {})
  },

  // Activating the layers-detail card by id (rather than pushing a route)
  // matches the existing deep-link path (`?cards=layers_detail:<id>`):
  // control-layers/index.vue's watchEffect resolves the layer from this same
  // card param via `resolveLayerById` and renders the detail card, so this
  // works whether or not the layers panel is already open.
  openLayerDetails: (layerId: string) => {
    useCardsStore().setCardState(CARD_LAYERS_DETAIL, true, layerId)
  },
}

/**
 * Not part of `AiNavigation` -- "waterbody" is fish vocabulary, not a
 * portable concept, and this component was always fish-specific
 * (`app/lib/ai/components/citation-waterbody.ts`, its only caller), so it
 * imports this directly rather than needing a dependency-injected callback.
 */
export const navigateToWaterbody = (id: string): void => {
  router
    .push({
      name: routeNames.WATERBODY_OVERVIEW,
      params: { id },
      state: { origin: CARD_ORIGINS.AI_GUIDE_RESPONSE_LINK },
    })
    .catch(() => {})
}

/**
 * `window.location.origin` isn't reliable here: this project's global jest
 * setup replaces `window.location` with a plain object carrying only
 * `pathname`/`href`, so `.origin` is `undefined` under test. Deriving it
 * from `.href` (which the polyfill does provide) via `URL` itself sidesteps
 * that gap instead of depending on the full `Location` interface.
 */
const currentOrigin = (): string => new URL(window.location.href).origin

/**
 * A real, absolute URL to a waterbody's overview page -- handed to the
 * model as a Markdown link's href (see `tool-query-waterbodies.ts`) instead
 * of an inert scheme. `citation-markdown-plugin.ts`'s `parseWaterbodyUrl`
 * is this function's inverse. Real (rather than a `cite://`-style token)
 * so that copying the chat message text out of the app -- to share it
 * elsewhere -- carries a link that still resolves to the right page there,
 * matching the pattern already used in Hunt (webmap-onxmaps#13370).
 */
export const buildWaterbodyUrl = (id: string): string => {
  const { href } = router.resolve({
    name: routeNames.WATERBODY_OVERVIEW,
    params: { id },
  })
  return `${currentOrigin()}${href}`
}

/**
 * The inverse of `buildWaterbodyUrl`: the waterbody id when `url` resolves
 * to that same route on this app's own origin, or `null` otherwise. A
 * link's origin is checked explicitly first -- `router.resolve` matches
 * against path shape alone, so without this check a same-shaped path on an
 * unrelated site would be treated as one of ours.
 */
export const parseWaterbodyUrl = (url: string): string | null => {
  let parsed: URL
  try {
    parsed = new URL(url, window.location.href)
  } catch {
    return null
  }
  if (parsed.origin !== currentOrigin()) {
    return null
  }

  const resolved = router.resolve(parsed.pathname + parsed.search)
  if (resolved.name !== routeNames.WATERBODY_OVERVIEW) {
    return null
  }
  const { id } = resolved.params
  return typeof id === 'string' ? id : null
}
